/**
 * A start and end on a wall calendar, each a date plus a time of day. The
 * range picker edits these; turning them into real instants is the caller's
 * job, since only the caller knows which time zone the wall clock is in.
 */
import {
  addDays,
  dateKeyParts,
  daysBetween,
  daysInMonth,
  MONTH_NAMES,
  toDateKey,
  weekdayOf,
  type DateKey,
} from './date-keys';
import { END_OF_DAY, START_OF_DAY } from './time-of-day';

export interface DateTimePoint {
  date: DateKey;
  /** Minutes after midnight. */
  minutes: number;
}

export interface DateTimeRange {
  start: DateTimePoint;
  end: DateTimePoint;
}

export type RangeEnd = 'start' | 'end';

/** Wall-clock minutes from `a` to `b`. */
export function minutesBetween(a: DateTimePoint, b: DateTimePoint): number {
  return daysBetween(a.date, b.date) * 1440 + (b.minutes - a.minutes);
}

export function isRangeInOrder({ start, end }: DateTimeRange): boolean {
  return minutesBetween(start, end) > 0;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * "8 days", "3 days, 1 hour", "2 hours, 30 min". An end at 11:59 PM counts as
 * the whole day. Null when the end is not after the start.
 */
export function formatRangeLength(range: DateTimeRange): string | null {
  let minutes = minutesBetween(range.start, range.end);
  if (minutes <= 0) return null;
  if (range.end.minutes === END_OF_DAY) minutes += 1;
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days) return hours ? `${plural(days, 'day')}, ${plural(hours, 'hour')}` : plural(days, 'day');
  if (hours) return mins ? `${plural(hours, 'hour')}, ${mins} min` : plural(hours, 'hour');
  return `${mins} min`;
}

export interface DayPickResult {
  range: DateTimeRange;
  /** Which end the next click on the calendar sets. */
  next: RangeEnd;
}

/**
 * A click on a calendar day. Picking the start moves on to the end, pushing the
 * end along if it would fall before the new start. A click before the start
 * while picking the end moves the start instead.
 */
export function pickDay(range: DateTimeRange, editing: RangeEnd, day: DateKey): DayPickResult {
  const { start, end } = range;
  if (editing === 'start') {
    return {
      range: { start: { ...start, date: day }, end: day > end.date ? { ...end, date: day } : end },
      next: 'end',
    };
  }
  if (day < start.date) return { range: { start: { ...start, date: day }, end }, next: 'end' };
  return { range: { start, end: { ...end, date: day } }, next: 'end' };
}

export interface QuickPick {
  label: string;
  start: DateKey;
  end: DateKey;
}

function thisWeekend(today: DateKey): { start: DateKey; end: DateKey } {
  const dow = weekdayOf(today);
  const friday =
    dow === 6 ? addDays(today, -1) : dow === 0 ? addDays(today, -2) : addDays(today, 5 - dow);
  return { start: friday < today ? today : friday, end: addDays(friday, 2) };
}

/** The preset ranges offered beside the calendar, worked out from today. */
export function quickPicks(today: DateKey): QuickPick[] {
  const { year, month } = dateKeyParts(today);
  const nextMonth = (month + 1) % 12;
  const nextMonthYear = month === 11 ? year + 1 : year;
  return [
    { label: 'Today only', start: today, end: today },
    { label: 'This weekend', ...thisWeekend(today) },
    { label: 'Next 7 days', start: today, end: addDays(today, 6) },
    { label: 'Next 14 days', start: today, end: addDays(today, 13) },
    {
      label: `Rest of ${MONTH_NAMES[month]}`,
      start: today,
      end: toDateKey(year, month, daysInMonth(year, month)),
    },
    {
      label: `All of ${MONTH_NAMES[nextMonth]}`,
      start: toDateKey(nextMonthYear, nextMonth, 1),
      end: toDateKey(nextMonthYear, nextMonth, daysInMonth(nextMonthYear, nextMonth)),
    },
  ];
}

/** A quick pick runs from the start of its first day to the end of its last. */
export function rangeFromQuickPick(pick: QuickPick): DateTimeRange {
  return {
    start: { date: pick.start, minutes: START_OF_DAY },
    end: { date: pick.end, minutes: END_OF_DAY },
  };
}

export function isQuickPickApplied(pick: QuickPick, range: DateTimeRange): boolean {
  return pick.start === range.start.date && pick.end === range.end.date;
}
